/**
 * @license uuid v8.3.2 (c) 2010-2020 Robert Kieffer
 */

/**
 * @fileoverview  The code in the generateV4 method was inspired by
 * similar code in uuid (hence the above copyright notice).
 */

import Mersenne from 'mersenne-twister';
import { stringify } from 'uuid';
import { PulseEnvironment } from '../globals';
import { DateService } from './date.service';

export class UUIDService {
    private mersenne?: Mersenne;
    private nativePRNG;

    constructor(
        private date: DateService,
        private env: PulseEnvironment,
    ) {
        this.nativePRNG = this.getNativePRNG();
    }

    generate() {
        // c: crypto.getRandomValues  /  m: Math.random
        const now = this.date.now();
        const entropy = this.nativePRNG ? 'c' : 'm';
        const version = 'a';
        const timestamp = now.toString(36);
        const id = this.generateV4();

        return `${version}.${entropy}.${timestamp}.${id}`;
    }

    private generateV4() {
        const randoms = this.randomValues(new Uint8Array(16));

        // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

        randoms[6] = ((randoms as any)[6] & 0x0f) | 0x40;
        randoms[8] = ((randoms as any)[8] & 0x3f) | 0x80;

        return stringify(randoms);
    }

    private randomValues(arr: Uint8Array) {
        if (this.nativePRNG) {
            return this.nativePRNG(arr);
        }

        if (!this.mersenne) {
            this.mersenne = new Mersenne(this.weakSeed());
        }

        let l = arr.length;
        while (l--) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            arr[l] = Math.floor(this.mersenne!.random() * 256);
        }

        return arr;
    }

    private weakSeed(): number[] {
        const randoms = Array(127)
            .fill(0)
            .map(() => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER));

        return [this.date.now(), ...randoms];
    }

    private getNativePRNG() {
        const win: {
            crypto?: Crypto;
            msCrypto?: Crypto;
        } = this.env.win;

        return (
            win.crypto?.getRandomValues?.bind(win.crypto) ||
            win.msCrypto?.getRandomValues?.bind(win.msCrypto) ||
            undefined
        );
    }
}
